import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { FaIdCard } from 'react-icons/fa';
import { FiArrowLeft, FiChevronLeft, FiChevronRight, FiLinkedin, FiMail, FiUsers } from 'react-icons/fi';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { SectionHeader } from '../ui/SectionHeader';

interface LeadershipSectionProps {
  showFullTeam?: boolean;
}

export const LeadershipSection: React.FC<LeadershipSectionProps> = ({ showFullTeam = false }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [currentSlide, setCurrentSlide] = useState(0);

  const leaders = [
    {
      name: 'فهد الحصان',
      title: 'المهندس المؤسس ومطور المنتج',
      institution: 'جامعة الملك سعود',
      expertise: 'هندسة البرمجيات وتطوير المنتجات التعليمية',
      description: 'يقود الرؤية الشاملة للمنصة من الاحتياج، التصميم إلى التنفيذ، ويضمن التوافق مع أفضل الممارسات في تطوير المنتجات التعليمية.',
      imageUrl: undefined as string | undefined,
      linkedinUrl: 'https://sa.linkedin.com/in/fahad-alhssan/ar',
      email: 'fahad@alkhreej.com',
      achievements: [
        'أكثر من 500 ألف طالب يستفيدون من المنصة',
        'عضو هيئة تدريس في جامعة الملك سعود',
        'خبرة +5 سنوات في تطوير المنتجات التقنية'
      ]
    },
    {
      name: 'أسامة العتي',
      title: 'الشريك المؤسس',
      institution: 'استشاري اشعة تشخيصية',
      expertise: 'الاستراتيجية وتطوير الأعمال',
      description: 'يجمع بين الخبرة المهنية والتجارية، مع اهتمام بمجال الإرشاد المهني، ويسهم في توجيه ضوء الخريج استراتيجيًا عبر تطوير الشراكات وتعزيز حضور المنصة واستكشاف فرصها المستقبلية.',
      email: 'osamah@alkhreej.com',
      achievements: [
        'خبرة تجارية في تطوير المشاريع وتحقيق الاستدامة',
        'بناء الشراكات وتمثيل المنصة في المحافل ذات العلاقة',
        'الإسهام في تطوير توجه المنصة واستكشاف فرص نموها'
      ]
    },
    {
      name: 'ربى الحسين',
      title: 'الشريك المؤسس',
      institution: 'باحثة شرعية وقانونية',
      expertise: 'الإرشاد الأكاديمي وتطوير المحتوى',
      description: 'تقود تطوير المحتوى والإرشاد في ضوء الخريج، مستندة إلى فهم عميق للجامعات والتخصصات ومتطلبات القبول، وتسهم في بناء تجربة أكثر وضوحًا وارتباطًا باحتياج الطلاب في رحلة اختيار التخصص.',
      email: 'ruba@alkhreej.com',
      achievements: [
        'خبرة في الإرشاد الأكاديمي وفهم مسارات القبول',
        'تطوير المحتوى وتبسيط المعلومات للطلاب',
        'الإسهام في تصميم تجربة الإرشاد وتطويرها'
      ]
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + leaders.length) % leaders.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % leaders.length);
  };


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section id="team" className="py-20 bg-gradient-subtle">
      <div className="section-container">
        <SectionHeader
          subtitle="فريق القيادة"
          title="خبرة متعددة التخصصات تدعم رؤيتنا"
          description="فريق متنوع من الخبراء الأكاديميين والمهنيين مع روابط مؤسسية قوية في القطاعات الحيوية"
          accent={true}
        />

        {showFullTeam ? (
          /* Full Team Carousel */
          <motion.div
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="mt-12 lg:mt-16"
          >
            <div className="max-w-2xl mx-auto relative">
              {/* Navigation Arrows */}
              {leaders.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center text-brand-primary hover:bg-brand-primary hover:text-white transition-all duration-300"
                  >
                    <FiChevronLeft size={20} />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center text-brand-primary hover:bg-brand-primary hover:text-white transition-all duration-300"
                  >
                    <FiChevronRight size={20} />
                  </button>
                </>
              )}

              {/* Leader Card */}
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                className="card-government group text-right"
              >
                {/* Profile Image */}
                <div className="w-24 h-24 rounded-full mb-6 ml-auto group-hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                  {leaders[currentSlide].imageUrl ? (
                    <img 
                      src={leaders[currentSlide].imageUrl} 
                      alt={leaders[currentSlide].name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-brand-primary to-government-navy flex items-center justify-center text-white text-2xl font-bold">
                      {leaders[currentSlide].name[0]}
                    </div>
                  )}
                </div>

                {/* Name & Title */}
                <div className="text-right mb-6">
                  <h3 className="text-xl font-bold text-government-navy mb-2 font-heading">
                    {leaders[currentSlide].name}
                  </h3>
                  <p className="text-brand-primary font-semibold mb-1">
                    {leaders[currentSlide].title}
                  </p>
                  <div className="flex items-center justify-start gap-2 text-sm text-gray-600">
                    <FaIdCard size={14} />
                    <span>{leaders[currentSlide].institution}</span>
                  </div>
                </div>

                {/* Expertise */}
                <div className="mb-4 text-right">
                  <span className="inline-block bg-brand-primary bg-opacity-10 text-brand-primary text-xs font-semibold px-3 py-1 rounded-full">
                    {leaders[currentSlide].expertise}
                  </span>
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  {leaders[currentSlide].description}
                </p>

                {/* Achievements */}
                <div className="space-y-2 mb-6">
                  {leaders[currentSlide].achievements.map((achievement: string, achievementIndex: number) => (
                    <div key={achievementIndex} className="flex items-start gap-2 justify-start">
                      <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-xs text-gray-600 text-right">{achievement}</span>
                    </div>
                  ))}
                </div>

                {/* Contact */}
                <div className="mt-6 pt-6 border-t border-gray-200 flex justify-start gap-4">
                  <a 
                    href={`mailto:${leaders[currentSlide].email}`}
                    className="w-8 h-8 bg-brand-primary bg-opacity-10 rounded-full flex items-center justify-center text-brand-primary hover:bg-brand-primary hover:text-white transition-all duration-200"
                  >
                    <FiMail size={14} />
                  </a>
                  {leaders[currentSlide].linkedinUrl && (
                    <a 
                      href={leaders[currentSlide].linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 bg-brand-primary bg-opacity-10 rounded-full flex items-center justify-center text-brand-primary hover:bg-brand-primary hover:text-white transition-all duration-200"
                    >
                      <FiLinkedin size={14} />
                    </a>
                  )}
                </div>
              </motion.div>

              {/* Dots Indicator */}
              {leaders.length > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  {leaders.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentSlide ? 'bg-brand-primary w-6' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          /* Single Leader (Fahd) with Redirect Button */
          <motion.div
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="mt-12 lg:mt-16"
          >
            <div className="max-w-2xl mx-auto">
              <motion.div
                variants={itemVariants}
                className="card-government group hover:scale-105 transition-all duration-300 text-right cursor-pointer"
                onClick={() => window.location.href = '/leadership'}
              >
                {/* Profile Image */}
                <div className="w-24 h-24 rounded-full mb-6 ml-auto group-hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                  {leaders[0].imageUrl ? (
                    <img 
                      src={leaders[0].imageUrl} 
                      alt={leaders[0].name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-brand-primary to-government-navy flex items-center justify-center text-white text-2xl font-bold">
                      {leaders[0].name[0]}
                    </div>
                  )}
                </div>

                {/* Name & Title */}
                <div className="text-right mb-6">
                  <h3 className="text-xl font-bold text-government-navy mb-2 font-heading">
                    {leaders[0].name}
                  </h3>
                  <p className="text-brand-primary font-semibold mb-1">
                    {leaders[0].title}
                  </p>
                  <div className="flex items-center justify-start gap-2 text-sm text-gray-600">
                    <FiUsers size={14} />
                    <span>{leaders[0].institution}</span>
                  </div>
                </div>

                {/* Expertise */}
                <div className="mb-4 text-right">
                  <span className="inline-block bg-brand-primary bg-opacity-10 text-brand-primary text-xs font-semibold px-3 py-1 rounded-full">
                    {leaders[0].expertise}
                  </span>
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  {leaders[0].description}
                </p>

                {/* Achievements */}
                <div className="space-y-2 mb-6">
                  {leaders[0].achievements.map((achievement: string, achievementIndex: number) => (
                    <div key={achievementIndex} className="flex items-start gap-2 justify-start">
                      <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-xs text-gray-600 text-right">{achievement}</span>
                    </div>
                  ))}
                </div>

                {/* Contact */}
                <div className="mt-6 pt-6 border-t border-gray-200 flex justify-start gap-4">
                  <a 
                    href={`mailto:${leaders[currentSlide].email}`}
                    className="w-8 h-8 bg-brand-primary bg-opacity-10 rounded-full flex items-center justify-center text-brand-primary hover:bg-brand-primary hover:text-white transition-all duration-200"
                  >
                    <FiMail size={14} />
                  </a>
                  {leaders[currentSlide].linkedinUrl && (
                    <a 
                      href={leaders[currentSlide].linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 bg-brand-primary bg-opacity-10 rounded-full flex items-center justify-center text-brand-primary hover:bg-brand-primary hover:text-white transition-all duration-200"
                    >
                      <FiLinkedin size={14} />
                    </a>
                  )}
                </div>
              </motion.div>

              {/* Redirect Button */}
              <motion.div
                variants={itemVariants}
                className="text-center mt-8"
              >
                <Link
                  to="/leadership"
                  className="inline-flex items-center gap-2 text-brand-primary hover:text-government-navy font-medium transition-colors duration-300 group"
                >
                  <span>تعرف أكثر على فريق القيادة</span>
                  <FiArrowLeft className="group-hover:translate-x-2 transition-transform duration-300" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}

      </div>
    </section>
  );
};